"use client";
import React, { Suspense, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "motion/react";
import {
  CalendarDays,
  Users,
  Clock,
  MapPin,
  Phone,
  ChevronRight,
  Plus,
  Search,
  Filter,
  ChefHat,
  Star,
  Navigation,
  X,
  Sparkles,
  CheckCircle,
  Clock4,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

// Import your stores (adjust paths as needed)
import { useRestaurantStore } from "@/stores/useRestaurantStore";
import { useCustomer } from "@/providers/UserProvider";
import { useBookings } from "@/features/bookings/hooks/useBookingQueries";
import { Customer, Restaurant, Booking } from "@/types/inde_menu";
import CustomerDashboardLayout from "../Layout/DashboardLayout";
import InputField from "@/components/input_field";

// Modern theme with vibrant but professional colors
const THEME = {
  colors: {
    primary: "#2563EB",
    primaryLight: "#3B82F6",
    primaryDark: "#1D4ED8",
    secondary: "#F59E0B",
    secondaryLight: "#FBBF24",
    secondaryDark: "#D97706",
    accent: "#8B5CF6",
    accentLight: "#A78BFA",
    accentDark: "#7C3AED",
    success: "#10B981",
    successLight: "#34D399",
    error: "#EF4444",
    errorLight: "#F87171",
    warning: "#F59E0B",
    info: "#3B82F6",
    lightBg: "#F8FAFC",
    darkText: "#1E293B",
    mutedText: "#64748B",
    cardBg: "#FFFFFF",
    white: "#FFFFFF",
    border: "#E2E8F0",
    glass: "rgba(255, 255, 255, 0.7)",
  },
  shadows: {
    sm: "0 1px 2px rgba(0,0,0,0.05)",
    md: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
    lg: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
    xl: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
    subtle: "0 2px 8px rgba(0,0,0,0.03)",
  },
  fonts: {
    heading: "'Inter', system-ui, sans-serif",
    body: "'Inter', system-ui, sans-serif",
  },
  borderRadius: {
    sm: "6px",
    md: "8px",
    lg: "12px",
    xl: "16px",
  },
  backdrop: {
    blur: "blur(12px)",
  },
};

export default function TableBookingsPage() {
  const { fetchRestaurantById } = useRestaurantStore();
  const { user } = useCustomer();
  const userProfile = user as Customer | null;
  const router = useRouter();
  const { data: queryBookings, isLoading: isBookingsLoading } = useBookings({ customerId: userProfile?.id });
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [restaurants, setRestaurants] = useState<Record<string, Restaurant>>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Fetch user bookings
  useEffect(() => {
    const load = async () => {
      if (!userProfile?.id) return;

      setLoading(true);
      try {
        const userBookings = queryBookings || [];
        setBookings(userBookings);
        setFilteredBookings(userBookings);

        // Fetch restaurant details for each booking
        const restaurantIds = Array.from(
          new Set(userBookings.map((b) => b.restaurantId))
        ).filter((id): id is string => typeof id === "string");

        const restaurantData: Record<string, Restaurant> = {};

        for (const id of restaurantIds) {
          try {
            const restaurant = await fetchRestaurantById(id);
            if (restaurant) {
              restaurantData[id] = restaurant;
            }
          } catch (error) {
            console.error(`Failed to fetch restaurant ${id}:`, error);
          }
        }

        setRestaurants(restaurantData);
      } catch (error) {
        toast.error("Failed to fetch your bookings");
        console.error("Error fetching bookings:", error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [userProfile?.id, fetchRestaurantById, queryBookings]);

  // Filter bookings based on search and filter criteria
  useEffect(() => {
    let result = bookings;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((booking) => {
        const restaurant = restaurants[booking.restaurantId];
        return restaurant?.name?.toLowerCase().includes(term);
      });
    }

    if (statusFilter !== "all") {
      result = result.filter((booking) => booking.status === statusFilter);
    }

    setFilteredBookings(result);
  }, [bookings, searchTerm, statusFilter, restaurants]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Unknown Date";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Unknown Date";
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return "Unknown Time";
    const [hoursStr, minutesStr] = timeString.split(":");
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr ?? "0");
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return "Unknown Time";
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "confirmed":
        return {
          color: THEME.colors.success,
          icon: CheckCircle,
          label: "Confirmed",
        };
      case "pending":
        return {
          color: THEME.colors.warning,
          icon: Clock4,
          label: "Pending",
        };
      case "cancelled":
        return {
          color: THEME.colors.error,
          icon: AlertCircle,
          label: "Cancelled",
        };
      default:
        return {
          color: THEME.colors.mutedText,
          icon: Clock4,
          label: status,
        };
    }
  };

  const handleBookAnother = () => {
    router.push("/vendors");
  };

  const handleBookAtRestaurant = (restaurantId: string) => {
    router.push(`/dashboards/customer/bookings/${restaurantId}`);
  };

  const handleViewBookingDetails = (bookingId: string) => {
    router.push(`/dashboards/customer/bookings/${bookingId}`);
  };

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
  };

  const fadeAnim = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.2 } },
  };

  const staggerChildren = {
    visible: {
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  // Skeleton Loader Component
  const BookingSkeleton = () => (
    <div className="rounded-xl p-5 bg-white border border-gray-200 animate-pulse">
      <div className="flex flex-col lg:flex-row lg:items-start gap-5">
        <div className="flex-grow">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <div className="h-6 w-40 bg-gray-200 rounded-md"></div>
            <div className="h-6 w-24 bg-gray-200 rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="h-5 w-5 bg-gray-200 rounded"></div>
                <div className="h-4 w-24 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
          <div className="h-4 w-48 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 w-32 bg-gray-200 rounded"></div>
        </div>
        <div className="flex flex-col gap-2 lg:items-end">
          <div className="h-9 w-28 bg-gray-200 rounded-lg"></div>
          <div className="h-9 w-28 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    </div>
  );

  return (
    <CustomerDashboardLayout>
      <Suspense fallback={<div>Loading...</div>}>
        <div className="min-h-screen bg-gray-50 pb-12">
          <div className="container mx-auto px-4 py-8">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeAnim}
              className="mb-8"
            >
              <div className="flex items-center gap-2 mb-2">
                <Sparkles
                  size={24}
                  className="text-blue-500"
                  fill="currentColor"
                />
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                  My Table Bookings
                </h1>
              </div>
              <p className="text-gray-600">
                View and manage your restaurant reservations
              </p>
            </motion.div>

            {/* Actions Bar */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeAnim}
              className="flex flex-col gap-4 mb-8"
            >
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-grow">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                  <InputField
                    type="text"
                    placeholder="Search by restaurant name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={toggleFilter}
                    className="md:hidden flex items-center gap-2 px-4 py-3 rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    <Filter size={20} className="text-gray-600" />
                    <span className="text-gray-700">Filter</span>
                  </button>

                  <div className="hidden md:flex items-center gap-2">
                    <Filter size={20} className="text-gray-600" />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700"
                    >
                      <option value="all">All Statuses</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="pending">Pending</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <button
                    onClick={handleBookAnother}
                    className="flex items-center justify-center gap-2 px-4 py-3 md:px-6 md:py-3 rounded-lg font-semibold text-white transition-all shadow-md hover:shadow-lg bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus size={20} />
                    <span className="hidden md:inline">Book Another Table</span>
                    <span className="md:hidden">Book Table</span>
                  </button>
                </div>
              </div>

              {/* Active filters indicator */}
              {(searchTerm || statusFilter !== "all") && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="flex items-center gap-3 flex-wrap"
                >
                  <span className="text-sm text-gray-500">Active filters:</span>
                  {searchTerm && (
                    <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm">
                      Search: "{searchTerm}"
                      <button
                        onClick={() => setSearchTerm("")}
                        className="ml-1 rounded-full hover:bg-blue-200 p-1"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  {statusFilter !== "all" && (
                    <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm">
                      Status: {statusFilter}
                      <button
                        onClick={() => setStatusFilter("all")}
                        className="ml-1 rounded-full hover:bg-blue-200 p-1"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  <button
                    onClick={clearFilters}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Clear all
                  </button>
                </motion.div>
              )}

              {/* Mobile filter dropdown */}
              <AnimatePresence>
                {isFilterOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="md:hidden overflow-hidden bg-white rounded-lg border border-gray-200 p-4"
                  >
                    <div className="mb-3 font-medium text-gray-700">
                      Filter by status
                    </div>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700"
                    >
                      <option value="all">All Statuses</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="pending">Pending</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Bookings List */}
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <BookingSkeleton key={i} />
                ))}
              </div>
            ) : filteredBookings.length === 0 ? (
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeAnim}
                className="text-center py-16 rounded-xl bg-white border border-gray-200 shadow-sm"
              >
                <div className="max-w-md mx-auto">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                    <CalendarDays className="text-blue-600" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {bookings.length === 0
                      ? "No Bookings Yet"
                      : "No Matching Bookings"}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {bookings.length === 0
                      ? "You haven't made any table bookings yet. Get started by booking your first table!"
                      : "Try adjusting your search or filter criteria to find your bookings."}
                  </p>
                  <button
                    onClick={handleBookAnother}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white transition-all shadow-md hover:shadow-lg bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus size={18} />
                    Book a Table
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                variants={staggerChildren}
                initial="hidden"
                animate="visible"
                className="grid gap-4"
              >
                <AnimatePresence>
                  {filteredBookings.map((booking) => {
                    const restaurant =
                      restaurants[String(booking.restaurantId)];
                    const statusConfig = getStatusConfig(booking.status);
                    const StatusIcon = statusConfig.icon;

                    return (
                      <motion.div
                        key={booking.id}
                        variants={fadeAnim}
                        layout
                        className="rounded-xl p-5 bg-white border border-gray-200 transition-all hover:shadow-md"
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start gap-5">
                          <div className="flex-grow">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-blue-50">
                                  <ChefHat
                                    size={20}
                                    className="text-blue-600"
                                  />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">
                                  {restaurant?.name || "Unknown Restaurant"}
                                </h3>
                              </div>
                              <div className="flex items-center gap-2 self-start sm:self-center">
                                <StatusIcon
                                  size={16}
                                  className={`
                                    ${
                                      booking.status === "confirmed"
                                        ? "text-green-600"
                                        : ""
                                    }
                                    ${
                                      booking.status === "pending"
                                        ? "text-amber-600"
                                        : ""
                                    }
                                    ${
                                      booking.status === "cancelled"
                                        ? "text-red-600"
                                        : ""
                                    }
                                  `}
                                />
                                <span
                                  className="px-3 py-1 rounded-full text-sm font-medium capitalize"
                                  style={{
                                    backgroundColor: `${statusConfig.color}20`,
                                    color: statusConfig.color,
                                  }}
                                >
                                  {statusConfig.label}
                                </span>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                              <div className="flex items-center gap-2 text-gray-700">
                                <CalendarDays
                                  size={18}
                                  className="text-gray-500"
                                />
                                <span className="font-medium">
                                  {formatDate(booking.date)}
                                </span>
                              </div>

                              <div className="flex items-center gap-2 text-gray-700">
                                <Clock size={18} className="text-gray-500" />
                                <span className="font-medium">
                                  {formatTime(booking.time)}
                                </span>
                              </div>

                              <div className="flex items-center gap-2 text-gray-700">
                                <Users size={18} className="text-gray-500" />
                                <span className="font-medium">
                                  {booking.partySize}{" "}
                                  {booking.partySize === 1 ? "Guest" : "Guests"}
                                </span>
                              </div>

                              <div className="flex items-center gap-2 text-gray-700">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{
                                    backgroundColor: statusConfig.color,
                                  }}
                                ></div>
                                <span className="font-medium capitalize">
                                  {booking.status}
                                </span>
                              </div>
                            </div>

                            {restaurant && (
                              <div className="flex flex-col sm:flex-row sm:items-center gap-4 text-sm text-gray-600 mb-4">
                                <div className="flex items-center gap-2">
                                  <MapPin size={16} />
                                  <span>
                                    {restaurant.address?.street},{" "}
                                    {restaurant.address?.city}
                                  </span>
                                </div>

                                {restaurant.contactInfo && (
                                  <div className="flex items-center gap-2">
                                    <Phone size={16} />
                                    <span>{restaurant.contactInfo.phone}</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {restaurant?.averageRating && (
                              <div className="flex items-center gap-1 mb-4">
                                <Star
                                  size={16}
                                  fill="#F59E0B"
                                  className="text-amber-500"
                                />
                                <span className="text-sm font-medium text-gray-900">
                                  {restaurant.averageRating}
                                </span>
                                <span className="text-sm text-gray-500">
                                  ({restaurant.totalReviews} reviews)
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-col gap-2 lg:items-end">
                            <button
                              onClick={() =>
                                handleViewBookingDetails(booking.id)
                              }
                              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all hover:gap-3 text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100"
                            >
                              View Details <ChevronRight size={16} />
                            </button>
                            {restaurant && (
                              <button
                                onClick={() =>
                                  handleBookAtRestaurant(restaurant.id)
                                }
                                className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all text-white bg-amber-600 hover:bg-amber-700"
                              >
                                <Plus size={16} />
                                Book Again
                              </button>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </motion.div>
            )}
          </div>
        </div>
      </Suspense>
    </CustomerDashboardLayout>
  );
}
